import { useEffect, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getCredentialExpiringExpiredMessage } from '@Helpers';

import { sendErrorEvent } from '@Analytics';
import { ClientConfiguration, ExpiringCredential } from '@Models';
import { AppDispatch, authorizationOperations, fetchExpiringCredentials, RootState } from '@Store';
import config from '../../config';
import { buildLockedMenu, buildMenu } from './menus';
import PlatformSidebar from './platformSidebar';
import { MenuItem } from './types';
import { SIDEBAR_EXPAND_STATE_KEY } from './useSidebarExpandState';

interface PlatformSidebarContainerProps {
  permissions: string[];
  configuration?: ClientConfiguration;
  expiresCredentials?: ExpiringCredential[];
  clearToken: () => void;
  locked?: boolean;
}

/**
 * Decorate the Credentials item with an expiring-credentials warning when
 * applicable. Walks into submenus too since Credentials is nested under
 * Configuration in default/gs1 flavors after the sidebar reorder.
 */
const decorateWithExpiringWarning = (
  items: MenuItem[],
  expiresCredentials: ExpiringCredential[] | undefined,
  credentialsLabel: string,
  tooltip: string | undefined,
): MenuItem[] => {
  const hasExpiring = !!expiresCredentials && expiresCredentials.length > 0;
  if (!hasExpiring) return items;

  const decorate = (item: MenuItem): MenuItem => {
    if (item.label === credentialsLabel) {
      return {
        ...item,
        extraIcon: <AlertTriangle className="size-4 text-warning-500" />,
        extraIconTooltip: tooltip,
      };
    }
    if (item.submenu) return { ...item, submenu: item.submenu.map(decorate) };
    return item;
  };

  return items.map(decorate);
};

const PlatformSidebarContainer = ({
  permissions,
  configuration,
  expiresCredentials,
  clearToken,
  locked = false,
}: PlatformSidebarContainerProps) => {
  const { logout } = useAuth0();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (locked) return;
    dispatch(fetchExpiringCredentials());
  }, [locked, dispatch]);

  useEffect(() => {
    // While a GS1 provider's workspace is being provisioned the user lives at
    // /onboarding (or sees SupplierOnboarding); don't preempt that with the
    // "no dashboards → /landing" guard. `dataReady` is a GS1-only field, so
    // gate the skip by flavor too — non-GS1 deployments never have this
    // provisioning state.
    if (config.isGs1Flavor && configuration?.dataReady === false) return;
    if (configuration?.dashboards?.length === 0 && location.pathname.includes('monitor')) {
      navigate('/landing');
    }
  }, [configuration, location.pathname, navigate]);

  const menuItems = useMemo<MenuItem[]>(() => {
    if (locked) return buildLockedMenu(t);
    const base = buildMenu({ configuration, permissions, t });
    return decorateWithExpiringWarning(
      base,
      expiresCredentials,
      t('Sidebar.Credentials'),
      getCredentialExpiringExpiredMessage(t, expiresCredentials),
    );
    // i18n.language is included so the menu rebuilds on language change
    // even when only the resolved t() output would change.
  }, [locked, configuration, permissions, expiresCredentials, t, i18n.language]);

  const onLogout = () => {
    try {
      localStorage.removeItem('popupMessage.dismissed');
      localStorage.removeItem(SIDEBAR_EXPAND_STATE_KEY);
    } catch (error) {
      // localStorage can throw under Safari Private Mode / quota-exceeded —
      // surface to CloudWatch so we know if this is happening at scale.
      sendErrorEvent(`localStorage cleanup failed on logout: ${(error as Error)?.message ?? 'unknown'}`);
    }
    if (config.isLogycaFlavor) {
      clearToken();
      window.location.href = config.REACT_APP_LOGYCA_LOGIN_URL;
      return;
    }
    logout({ returnTo: `${window.location.origin}` });
  };

  return (
    <PlatformSidebar
      menuItems={menuItems}
      onLogout={onLogout}
      showResources={!!configuration?.resources}
      logo={configuration?.logo}
      locked={locked}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  const { authorization, client, credentials } = state;
  return {
    permissions: authorization.permissions,
    configuration: client?.configuration,
    expiresCredentials: (credentials?.expires_credentials ?? []) as ExpiringCredential[],
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  clearToken: () => dispatch(authorizationOperations.clearToken()),
});

export default connect(mapStateToProps, mapDispatchToProps)(PlatformSidebarContainer);
